import './time-criteria.scss';
import React, { useMemo, useState } from 'react';
import { Input, Menu, Popover } from 'antd';
import { DatePickerWrapper } from '../../../../shared/date-picker-wrapper/date-picker-wrapper';
import { insightsDateHourFormat, subtractDaysFromToday } from '../../../../../utils/datesUtils';
import { observer } from 'mobx-react-lite';
import { CUSTOM_TIME_RANGE } from '../../../../../utils/constants';
import { TimeDetails } from '../../../../../dtos/search-criteria.dto';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import Arrow from '@svg/chevron-down_20.svg?react';


interface TimeCriteriaProps {
    options: { value: string; label: string }[];
    setTime: (time: TimeDetails) => void;
    value: TimeDetails;
    allowedDaysAgo?: number;
    disabled?: boolean;
}

export const TimeCriteria: React.FC<TimeCriteriaProps> = observer(({ options, setTime, value, allowedDaysAgo, disabled }) => {
    const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
    const [isDropDownOpen, setIsDropDownOpen] = useState(false);
    const DATE_PICKER_CLASS = '.date-picker-component';

    const selectedTime = useMemo(() => {
        if (value.timeRange !== CUSTOM_TIME_RANGE) {
            return value.timeRange;
        }
        const convertedStartDate = insightsDateHourFormat(value.startDate!);
        const convertedEndDate = insightsDateHourFormat(value.endDate!);
        return `${convertedStartDate} - ${convertedEndDate}`;
    }, [value]);

    const handleSelect = (selectedValue: string) => {
        if (CUSTOM_TIME_RANGE === selectedValue) {
            setIsDatePickerVisible(true);
        } else {
            const updatedValue: TimeDetails = { ...value, timeRange: selectedValue };
            setTime(updatedValue);
            closeDatePickerAndDropdown();
        }
    };

    const applyChanges = (dates: [Date, Date]) => {
        setTime({ timeRange: CUSTOM_TIME_RANGE, startDate: dates[0], endDate: dates[1] });
        closeDatePickerAndDropdown();
    };

    const closeDatePickerAndDropdown = () => {
        setIsDatePickerVisible(false);
        setIsDropDownOpen(false);
    };

    const popoverDatePickerContent =
        <DatePickerWrapper initialStartDate={value.startDate!}
                           initialEndDate={value.endDate!}
                           maxDate={subtractDaysFromToday(0)}
                           allowedDaysAgo={allowedDaysAgo}
                           cancelDatePicker={() => setIsDatePickerVisible(false)}
                           enableHoursEditing={true}
                           applyChanges={applyChanges}
                           onClickOutside={() => setIsDatePickerVisible(false)}/>;

    const items: ItemType[] = [ ...options.map((item) => ({
        key: item.value,
        label: item.label,
        onClick: () => handleSelect(item.value),
    })),
        {
            key: CUSTOM_TIME_RANGE,
            label: <Popover destroyTooltipOnHide={true}
                            content={popoverDatePickerContent}
                            showArrow={false}
                            overlayClassName={'date-picker-overlay-popover'}
                            trigger={'click'}
                            open={isDatePickerVisible}
                            onOpenChange={(open) => setIsDatePickerVisible(!open)}
                            placement={'rightBottom'}>
                <div className={'with-divide'}>{CUSTOM_TIME_RANGE}</div>
                <Arrow />
            </Popover>,
            onClick: (info) => {
                if ((info.domEvent.nativeEvent.target as any).closest(DATE_PICKER_CLASS)) return;
                handleSelect(CUSTOM_TIME_RANGE);
            },
            className: 'custom-option',
        }];

    const menu = <Menu items={items} selectedKeys={[value.timeRange!]} style={{ width: `210px` }}/>;

    return <Popover content={menu} trigger={'click'}
                    placement={'bottomLeft'}
                    open={isDropDownOpen}
                    showArrow={false}
                    destroyTooltipOnHide={true}
                    onOpenChange={(open) => setIsDropDownOpen(open)}
                    overlayClassName={'time-menu popover-overlay-menu'}>
        <div className={'flex time-input-wrapper'}>
            <Input disabled={disabled} value={selectedTime} className={'time-input'}/>
            <Arrow />
        </div>
    </Popover>;
});